import { useEffect, useState } from "react";
import { shallowEqual } from "react-redux";
import { useHistory, useLocation } from 'react-router-dom';
import { hasValue } from '../../../services/util/CommonUtil';
import UrlParamsUtil from "../../../services/util/UrlParamsUtil";
import employeeService from '../../vdo-ticket/services/manage/EmployeeService';

const useAssociateSearch = (onlyMyOffices, impersonateUser) => {

    let location = useLocation();
    let basePath = location.pathname
    let history = useHistory();

    const { s: searchString, sel: selectedAgent, selOffice: selectedAgentOffice, sort, rev: reverse } = UrlParamsUtil.getQueryParams(location.search)

    const isReversed = reverse === 'true'
    const sortBy = sort || 'name'

    const [searching, setSearching] = useState(false);
    const [agentList, setAgentList] = useState([]);

    const buildSearchPath = (s, sa, so, sb, r) => {
        let parts = [basePath]

        if (s) {
            if (s) parts.push(`?s=${s}`)
            if (sa) parts.push(`&sel=${sa}`)
            if (so) parts.push(`&selOffice=${so}`)
            if (sb) parts.push(`&sort=${sb}`)
            if (r) parts.push(`&rev=${Boolean(r)}`)
        }

        parts.push(location.hash)
        return parts.join('')
    }

    const setSearch = (newSearch) => {
        if (newSearch === searchString) {
            // if search term hasn't changed, updating the url won't do anything in this case.
            // since the side effect can't be triggered this way, manually call the search
            performSearch()
        } else {
            history.push(buildSearchPath(newSearch, selectedAgent, selectedAgentOffice, sortBy, isReversed))
        }
    }

    const setSort = (newSortBy) => {
        if (newSortBy === sortBy) {
            // same sort key, reverse the sort order
            history.push(buildSearchPath(searchString, selectedAgent, selectedAgentOffice, sortBy, !isReversed))
        } else {
            // new sort, switch to that sort and reset back to ascending
            history.push(buildSearchPath(searchString, selectedAgent, selectedAgentOffice, newSortBy))
        }
    }

    const setSelected = (newId, newOfficeId) => {
        if ((!newId) && (typeof (selectedAgent) !== 'undefined')) {
            history.push(buildSearchPath(searchString, newId, newOfficeId, sortBy, isReversed))
        }
        else if (newId || selectedAgent) {
            history.push(buildSearchPath(searchString, newId, newOfficeId, sortBy, isReversed))
        }
    }

    const performSearch = async () => {
        setSearching(true)
        try {
            let employees = await employeeService.searchEmployees(searchString, onlyMyOffices, impersonateUser)
            if (employees && !shallowEqual(employees, agentList)) {
                sortAndSetAgents(employees)
            }
        } catch (ex) {
            console.log('error while searching ' + ex.message)
        } finally {
            setSearching(false)
        }        
    }

    const nameSort = (a, b) => {
        var nameA = a.firstName.toUpperCase();
        var nameB = b.firstName.toUpperCase();
        if (nameA < nameB) {
            return isReversed ? 1 : -1;
        }
        if (nameA > nameB) {
            return isReversed ? -1 : 1;
        }

        // if we reach here, sort by last name
        nameA = a.lastName.toUpperCase();
        nameB = b.lastName.toUpperCase();

        if (nameA < nameB) {
            return isReversed ? 1 : -1;
        }
        if (nameA > nameB) {
            return isReversed ? -1 : 1;
        }

        // perfect match! zomg!
        return 0;
    }

    const officeSort = (a, b) => {
        return isReversed ? a.officeId - b.officeId : b.officeId - a.officeId
    }

    const roleSort = (a, b) => {
        var roleA = (a.jobTitle || '').toUpperCase();
        var roleB = (b.jobTitle || '').toUpperCase();
        if (roleA < roleB) {
            return isReversed ? 1 : -1;
        }
        if (roleA > roleB) {
            return isReversed ? -1 : 1;
        }

        // perfect match! zomg!
        return 0;
    }

    const autoAssignmentSort = (a, b) => {
        let countA = 0
        if (a.isRteAssignable) countA+=2
        if (a.isVirAssignable) countA++

        let countB = 0
        if (b.isRteAssignable) countB+=2
        if (b.isVirAssignable) countB++

        return isReversed ? countA - countB : countB - countA;
    }

    const sortAndSetAgents = agents => {
        if (sortBy === 'office') {
            agents.sort(officeSort)
        } else if (sortBy === 'role') {
            agents.sort(roleSort)
        } else if (sortBy === 'auto-assignment') {
            agents.sort(autoAssignmentSort)
        } else {
            agents.sort(nameSort)
        }

        if (!shallowEqual(agents, agentList)) {
            setAgentList(agents)
        }
    }

    useEffect(() => {
        // if search params have changed, call the search api
        if (!searching && hasValue(searchString)) {
            performSearch()
        }
        // clear selection on new search string
        setSelected(null)

    }, [searchString])

    useEffect(() => {
        // copy agents array, sort it, then set it
        let newAgents = [].concat(agentList)
        sortAndSetAgents(newAgents)
    }, [sortBy, reverse])

    return { setSearch, setSort, setSelected, searching, agentList, selectedAgent, selectedAgentOffice, searchString, sortBy, isReversed, sortAndSetAgents }
}

export default useAssociateSearch